import gql from 'graphql-tag'
import {
  ActivityFilter,
  BaseSlice_ActivityFilterListItemFragment,
  JobType,
} from 'generated/graphql'
import { TFilterKey } from '@app.feature/activityFilter/modules/constant'
import { ActivityTypeID } from 'constants/enums'
import { TActivityFilterItem, TStoreSlice } from '../storeActivityFilter'

gql`
  fragment BaseSlice_activityFilterItemById on ActivityFilterItemByID {
    id
    label: name
  }

  fragment BaseSlice_ActivityFitlerItemByRange on ActivityFitlerItemByRange {
    label: name
    from
    to
  }

  fragment BaseSlice_activityFilterListItem on ActivityFilterListItem {
    name: type
    items {
      ... on ActivityFilterItemByID {
        ...BaseSlice_activityFilterItemById
      }
      ... on ActivityFitlerItemByRange {
        ...BaseSlice_ActivityFitlerItemByRange
      }
    }
  }
`

export type TActivityFilterStoreFilterBy = Pick<
  ActivityFilter,
  TFilterKey | 'q' | 'activityTypeID'
>

export type TBaseSlice = {
  activityType: ActivityTypeID
  setActivityType: (activityType: ActivityTypeID) => void
  initFilterBy: (options: {
    filterBy: TActivityFilterStoreFilterBy
    filterData: BaseSlice_ActivityFilterListItemFragment[]
    // 직무 카테고리의 특수성 때문에 필요한 예외처리
    especiallyLabelMaps?: {
      key: TFilterKey
      getLabelFunction: (id: string) => string
    }[]
  }) => void
  getFilterBy: () => TActivityFilterStoreFilterBy
}

const createBaseSlice: TStoreSlice<TBaseSlice> = (set, get) => ({
  activityType: ActivityTypeID.ACTIVITY,
  setActivityType: (activityType) => {
    set({
      activityType,
    })
  },
  initFilterBy: ({ filterBy, filterData, especiallyLabelMaps }) => {
    const parseFilterData = (
      key: TFilterKey,
      filterByList?: string[] | null,
    ): TActivityFilterItem[] => {
      const especiallyLabelMap = especiallyLabelMaps?.find(
        (map) => map.key === key,
      )

      if (especiallyLabelMap) {
        return (
          filterByList?.map((filterId) => {
            const label = especiallyLabelMap.getLabelFunction(filterId)

            return {
              id: filterId,
              label,
            }
          }) || []
        )
      }

      const targetFilterData = filterData.find(({ name }) => name === key)

      if (!targetFilterData) {
        return []
      }

      return (
        filterByList?.map((filterId) => {
          const data = targetFilterData.items?.find(
            (item) =>
              item?.__typename === 'ActivityFilterItemByID' &&
              item.id === filterId,
          )

          return {
            id: filterId,
            label:
              data?.__typename === 'ActivityFilterItemByID' ? data.label : '',
          }
        }) || []
      )
    }

    set((state) => ({
      activityType: (filterBy.activityTypeID ||
        state.activityType) as ActivityTypeID,
      keyword: filterBy.q ?? state.keyword,
      selectedFilter: {
        categoryIDs: parseFilterData('categoryIDs', filterBy.categoryIDs),
        jobTypes: parseFilterData('jobTypes', filterBy.jobTypes as string[]),
        orgTypeIDs: parseFilterData('orgTypeIDs', filterBy.orgTypeIDs),
        regionIDs: parseFilterData('regionIDs', filterBy.regionIDs),
        benefitIDs: parseFilterData('benefitIDs', filterBy.benefitIDs),
        interestIDs: parseFilterData('interestIDs', filterBy.interestIDs),
        period: parseFilterData('period', filterBy.period as string[]),
        reward: parseFilterData('reward', filterBy.reward as string[]),
        targetIDs: parseFilterData('targetIDs', filterBy.targetIDs),
        internTypeIds: parseFilterData('internTypeIds', filterBy.internTypeIds),
      },
    }))
  },
  getFilterBy: () => {
    const { activityType, selectedFilter, keyword } = get()

    const parseIds = (
      filterItem: TActivityFilterItem[],
    ): string[] | undefined => {
      if (filterItem.length === 0) return undefined
      return filterItem.map(({ id }) => id)
    }

    const parseFromTo = (
      filterItem: TActivityFilterItem[],
    ): Pick<TActivityFilterItem, 'from' | 'to'> | undefined => {
      const targetItem = filterItem?.[0]
      if (!targetItem) {
        return undefined
      }
      const { from, to } = targetItem
      if (!from && !to) {
        return undefined
      }
      return {
        from,
        to,
      }
    }

    const filterResult: TActivityFilterStoreFilterBy = {
      activityTypeID: String(activityType),
      q: keyword,
      categoryIDs: parseIds(selectedFilter.categoryIDs),
      jobTypes: parseIds(selectedFilter.jobTypes) as JobType[],
      orgTypeIDs: parseIds(selectedFilter.orgTypeIDs),
      internTypeIds: parseIds(selectedFilter.internTypeIds),
      regionIDs: parseIds(selectedFilter.regionIDs),
      benefitIDs: parseIds(selectedFilter.benefitIDs),
      interestIDs: parseIds(selectedFilter.interestIDs),
      period: parseFromTo(selectedFilter.period),
      reward: parseFromTo(selectedFilter.reward),
      targetIDs: parseIds(selectedFilter.targetIDs),
    }

    return filterResult
  },
})

export default createBaseSlice
